/**
 * ТЗ 0103, пакет 2 — «Курсы»: вывод одной фразой и помощники вкладки.
 *
 *  - `marketingCoursesInsight` — «Растут: нет. Падают: 5 из 5 — CRM-лидов
 *    меньше на 25–63 %.» по тем же строкам и правилам, что чипы «Лиды
 *    растут / падают» (`marketingCourseQuickFilter`); без сравнения — без
 *    дельт (лидеры по лидам и продажам), малая база — «данных мало»;
 *  - `marketingCourseSpark*` — спарклайн курса в строке таблицы (вместо
 *    малых графиков «Курсы во времени»): столбики лидов и точки продаж на
 *    ОБЩЕЙ шкале всех курсов;
 *  - `marketingUnservedTopics` — «Спрос без курса»: топ тем по спросу и
 *    «ещё N».
 */
import { marketingDeltaConfidence } from './marketingCompareModel';
import {
  IMarketingInsight,
  IMarketingInsightPart,
  marketingInsightPart,
  MarketingInsightParam,
  MarketingInsightSegment,
} from './marketingInsightsModel';
import {
  formatMarketingCount,
  formatMarketingSales,
  marketingNumber,
  MARKETING_NONE_KEY,
} from './marketingModel';
import {
  IMarketingCourseFilterRow,
  marketingCourseQuickFilter,
} from './marketingQuickFilters';
import { IMarketingSeriesPoint } from './types';

/* ── Вывод «Курсов» ──────────────────────────────────────────────────── */

/** Строка курса для вывода: те же строки, что считают чипы (до фильтра). */
export interface IMarketingCourseInsightRow extends IMarketingCourseFilterRow {
  key: string;
  label?: string | null;
}

export interface IMarketingCoursesInsightInput {
  /**
   * Строки ДО быстрого фильтра; без сравнения — с `previous: null` (как у
   * чипов): «растут / падают» тогда не считаются.
   */
  rows: ReadonlyArray<IMarketingCourseInsightRow>;
  /** Сервер прислал окно сравнения. */
  compareOn: boolean;
  lang: string;
}

/** Изменение CRM-лидов строки к окну сравнения, целыми процентами по модулю. */
export const marketingCourseLeadsChangePct = (
  row: IMarketingCourseFilterRow,
): number | null => {
  const previous = row.previous?.crmLeads ?? null;
  if (previous === null || previous <= 0) return null;
  return Math.round(
    (Math.abs(row.metrics.crmLeads - previous) / previous) * 100,
  );
};

export interface IMarketingCourseChangeRange {
  /** Наименьший и наибольший процент; `null` — процентов нет. */
  from: number | null;
  to: number | null;
  /** Строк, выросших с нуля (процента у них нет). */
  fromZero: number;
}

/** Разброс изменения лидов по строкам: «25–63 %» и «с нуля: N». */
export const marketingCourseChangeRange = (
  rows: ReadonlyArray<IMarketingCourseFilterRow>,
): IMarketingCourseChangeRange => {
  const pcts = rows
    .map(marketingCourseLeadsChangePct)
    .filter((pct): pct is number => pct !== null);
  return {
    from: pcts.length ? Math.min(...pcts) : null,
    to: pcts.length ? Math.max(...pcts) : null,
    fromZero: rows.filter((row) => (row.previous?.crmLeads ?? null) === 0)
      .length,
  };
};

const rangePart = (
  range: IMarketingCourseChangeRange,
  lang: string,
): IMarketingInsightPart | null => {
  if (range.from === null || range.to === null) return null;
  return range.from === range.to
    ? marketingInsightPart('marketing.insights.courses.pct', {
        pct: formatMarketingCount(range.from, lang),
      })
    : marketingInsightPart('marketing.insights.courses.range', {
        from: formatMarketingCount(range.from, lang),
        to: formatMarketingCount(range.to, lang),
      });
};

const salesOf = (row: IMarketingCourseFilterRow) =>
  marketingNumber(row.metrics.sales) ?? 0;

/**
 * Курсы с наибольшим значением (без «Курс не определён»): ничья — все
 * поровну первые; у всех ноль — никого.
 */
const leadersBy = (
  rows: ReadonlyArray<IMarketingCourseInsightRow>,
  value: (row: IMarketingCourseInsightRow) => number,
): IMarketingCourseInsightRow[] => {
  const real = rows.filter(
    (row) => row.key !== MARKETING_NONE_KEY && value(row) > 0,
  );
  const max = Math.max(0, ...real.map(value));
  return max > 0 ? real.filter((row) => value(row) === max) : [];
};

const nameOf = (row: IMarketingCourseInsightRow) => row.label || row.key;

/** Имена лидеров: «A»; «A» и «B»; «A» и ещё N. */
const namesPart = (
  list: ReadonlyArray<IMarketingCourseInsightRow>,
  lang: string,
): IMarketingInsightPart => {
  if (list.length === 1) {
    return marketingInsightPart('marketing.insights.courses.name', {
      name: nameOf(list[0]),
    });
  }
  if (list.length === 2) {
    return marketingInsightPart('marketing.insights.courses.namePair', {
      a: nameOf(list[0]),
      b: nameOf(list[1]),
    });
  }
  return marketingInsightPart('marketing.insights.courses.nameMore', {
    name: nameOf(list[0]),
    n: formatMarketingCount(list.length - 1, lang),
  });
};

/** Число лидера: «29»; при ничьей — «по 8». */
const valuePart = (text: string, tie: boolean): MarketingInsightParam =>
  tie
    ? marketingInsightPart('marketing.insights.courses.each', { n: text })
    : text;

/** Без сравнения: лидеры окна по лидам и продажам — без дельт. */
const insightWithoutCompare = (
  rows: ReadonlyArray<IMarketingCourseInsightRow>,
  lang: string,
): IMarketingInsight => {
  const leads = rows.reduce((sum, row) => sum + row.metrics.crmLeads, 0);
  const sales = rows.reduce((sum, row) => sum + salesOf(row), 0);
  if (leads === 0 && sales === 0) {
    return {
      tone: 'neutral',
      segments: [marketingInsightPart('marketing.insights.courses.empty'), '.'],
    };
  }
  if (
    marketingDeltaConfidence(leads, 0) === 'smallBase' &&
    marketingDeltaConfidence(sales, 0) === 'smallBase'
  ) {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.courses.smallBaseNoCompare', {
          n: formatMarketingCount(leads, lang),
          sales: formatMarketingSales(sales, lang),
        }),
        '.',
      ],
    };
  }
  const byLeads = leadersBy(rows, (row) => row.metrics.crmLeads);
  const bySales = leadersBy(rows, salesOf);
  const leadsText = (list: IMarketingCourseInsightRow[]) =>
    valuePart(
      formatMarketingCount(list[0].metrics.crmLeads, lang),
      list.length > 1,
    );
  const salesText = (list: IMarketingCourseInsightRow[]) =>
    valuePart(formatMarketingSales(salesOf(list[0]), lang), list.length > 1);
  const segments: MarketingInsightSegment[] = [];
  if (
    byLeads.length === 1 &&
    bySales.length === 1 &&
    byLeads[0].key === bySales[0].key
  ) {
    // Один курс впереди и по лидам, и по продажам — одним куском.
    segments.push(
      marketingInsightPart('marketing.insights.courses.leadersBoth', {
        name: namesPart(byLeads, lang),
        leads: formatMarketingCount(byLeads[0].metrics.crmLeads, lang),
        sales: formatMarketingSales(salesOf(bySales[0]), lang),
      }),
    );
  } else {
    if (byLeads.length) {
      segments.push(
        marketingInsightPart('marketing.insights.courses.leaders', {
          name: namesPart(byLeads, lang),
          n: leadsText(byLeads),
        }),
      );
    }
    if (bySales.length) {
      if (segments.length) segments.push(', ');
      segments.push(
        marketingInsightPart(
          byLeads.length
            ? 'marketing.insights.courses.leadersSales'
            : 'marketing.insights.courses.leadersSalesOnly',
          { name: namesPart(bySales, lang), n: salesText(bySales) },
        ),
      );
    }
  }
  if (segments.length) segments.push('. ');
  segments.push(
    marketingInsightPart('marketing.insights.courses.noCompare'),
    '.',
  );
  return { tone: 'info', segments };
};

/**
 * Вывод «Курсов» одной фразой. Со сравнением — «Растут: … Падают: …» по
 * CRM-лидам: те же строки и то же правило, что числа на чипах «Лиды
 * растут / падают» (без «Курс не определён» и курсов с базой меньше 10
 * лидов — F-E4), и разброс процентов — как дельты таблицы (целыми);
 * лидов в обоих окнах меньше 10 — «данных мало». Без сравнения — лидеры
 * окна по лидам и продажам, без дельт. Строк нет — `null` (у вкладки своё
 * пустое состояние).
 */
export const marketingCoursesInsight = ({
  rows,
  compareOn,
  lang,
}: IMarketingCoursesInsightInput): IMarketingInsight | null => {
  if (!rows.length) return null;
  if (!compareOn) return insightWithoutCompare(rows, lang);
  const leads = rows.reduce((sum, row) => sum + row.metrics.crmLeads, 0);
  const previous = rows.reduce(
    (sum, row) => sum + (row.previous?.crmLeads ?? 0),
    0,
  );
  if (marketingDeltaConfidence(leads, previous) === 'smallBase') {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.courses.smallBase', {
          n: formatMarketingCount(leads, lang),
          prev: formatMarketingCount(previous, lang),
        }),
        '.',
      ],
    };
  }
  // F-E4: «из N» — настоящих курсов: «Курс не определён» не растёт и не
  // падает (его нет в чипах), значит, и в счёт не входит.
  const total = formatMarketingCount(
    rows.filter((row) => row.key !== MARKETING_NONE_KEY).length,
    lang,
  );
  const growing = rows.filter((row) =>
    marketingCourseQuickFilter('growing', row, null),
  );
  const falling = rows.filter((row) =>
    marketingCourseQuickFilter('falling', row, null),
  );
  const segments: MarketingInsightSegment[] = [];
  if (!growing.length) {
    segments.push(
      marketingInsightPart('marketing.insights.courses.growingNone'),
    );
  } else {
    const range = marketingCourseChangeRange(growing);
    const by = rangePart(range, lang);
    segments.push(
      marketingInsightPart('marketing.insights.courses.growing', {
        n: formatMarketingCount(growing.length, lang),
        total,
      }),
      ' — ',
    );
    if (by) {
      segments.push(
        marketingInsightPart('marketing.insights.courses.moreBy', {
          range: by,
        }),
      );
    }
    if (range.fromZero > 0) {
      if (by) segments.push(', ');
      segments.push(
        marketingInsightPart('marketing.insights.courses.fromZero', {
          n: formatMarketingCount(range.fromZero, lang),
        }),
      );
    }
  }
  segments.push('. ');
  if (!falling.length) {
    segments.push(
      marketingInsightPart('marketing.insights.courses.fallingNone'),
    );
  } else {
    const by = rangePart(marketingCourseChangeRange(falling), lang);
    segments.push(
      marketingInsightPart('marketing.insights.courses.falling', {
        n: formatMarketingCount(falling.length, lang),
        total,
      }),
    );
    if (by) {
      segments.push(
        ' — ',
        marketingInsightPart('marketing.insights.courses.lessBy', {
          range: by,
        }),
      );
    }
  }
  segments.push('.');
  let tone: IMarketingInsight['tone'] = 'neutral';
  if (falling.length > growing.length) tone = 'bad';
  else if (growing.length > falling.length) tone = 'good';
  return { tone, segments };
};

/**
 * Строк «только прогноз» (курс без активности в окне, но с прогнозом
 * месяца) — их таблица курсов добавляет к строкам окна; чип «Все» считает
 * так же, как строки таблицы (финальная проверка 0103, F-E18).
 */
export const marketingCourseForecastOnlyCount = (
  rows: ReadonlyArray<{ key: string }> | null | undefined,
  forecast: ReadonlyArray<{ familySlug: string }> | null | undefined,
): number =>
  rows && forecast
    ? forecast.filter(
        (item) => !rows.some((row) => row.key === item.familySlug),
      ).length
    : 0;

/* ── Спарклайн курса в строке таблицы ────────────────────────────────── */

export interface IMarketingCourseSparkPoint {
  key: string;
  /** CRM-лиды корзины; `null` — корзины нет (пропуск ≠ ноль). */
  leads: number | null;
  sales: number | null;
}

/** Ряд строки курса → точки спарклайна (лиды и продажи по шагу). */
export const marketingCourseSparkPoints = (
  series: ReadonlyArray<IMarketingSeriesPoint> | null | undefined,
): IMarketingCourseSparkPoint[] =>
  (series ?? []).map((point) => ({
    key: point.period,
    leads: point.metrics.crmLeads ?? null,
    sales: marketingNumber(point.metrics.sales),
  }));

export interface IMarketingCourseSparkScale {
  maxLeads: number;
  maxSales: number;
}

/**
 * Общая шкала спарклайнов всех курсов: курс с 3 лидами не выглядит как
 * курс с 300 (своя шкала растянула бы малый курс до высоты большого).
 */
export const marketingCourseSparkScale = (
  rows: ReadonlyArray<{ series?: ReadonlyArray<IMarketingSeriesPoint> | null }>,
): IMarketingCourseSparkScale => {
  const points = rows.flatMap((row) => marketingCourseSparkPoints(row.series));
  return {
    maxLeads: Math.max(1, ...points.map((point) => point.leads ?? 0)),
    maxSales: Math.max(1, ...points.map((point) => point.sales ?? 0)),
  };
};

/* ── «Спрос без курса» ───────────────────────────────────────────────── */

/** Тем названо в «Спросе без курса»; остальные — «ещё N». */
export const MARKETING_UNSERVED_NAMED = 3;

export interface IMarketingUnservedTopic {
  key: string;
  label: string;
  searchVolume: number;
  hasCourse: boolean;
}

export interface IMarketingUnservedSummary<T extends IMarketingUnservedTopic> {
  /** Тем без нашего курса. */
  count: number;
  /** Запросов в месяц по ним всем. */
  volume: number;
  /** Самые искомые — по убыванию спроса. */
  top: T[];
  /** Сколько тем не названо. */
  more: number;
}

/**
 * Темы со спросом, по которым у нас нет курса: сколько, сколько запросов,
 * топ по спросу (`named`) и «ещё N». Порядок ответа сервера не важен —
 * сортируем по спросу, при равенстве — по названию.
 */
export const marketingUnservedTopics = <T extends IMarketingUnservedTopic>(
  topics: ReadonlyArray<T>,
  named: number = MARKETING_UNSERVED_NAMED,
): IMarketingUnservedSummary<T> => {
  const unserved = topics
    .filter((topic) => !topic.hasCourse)
    .sort(
      (a, b) =>
        b.searchVolume - a.searchVolume || a.label.localeCompare(b.label),
    );
  const top = unserved.slice(0, named);
  return {
    count: unserved.length,
    volume: unserved.reduce((sum, topic) => sum + topic.searchVolume, 0),
    top,
    more: unserved.length - top.length,
  };
};
